/**
 * Shared framer-motion primitives for OMNICREATE.
 *
 * Single source of truth for easing, durations, and reusable variants so
 * every panel/page animates on the same rhythm instead of each screen
 * inventing its own timing. Import variants directly for one-off motion.*
 * elements, or use the wrapper components in @/components/motion for the
 * common cases (scroll reveal, stagger lists, page transitions).
 */

// Expo-out: fast start, long settle. Reads as precise rather than bouncy,
// which fits the technical-drawing brand better than a spring.
export const EASE_OUT = [0.16, 1, 0.3, 1];
export const EASE_IN_OUT = [0.65, 0, 0.35, 1];

export const DURATION = {
  fast: 0.25,
  base: 0.45,
  slow: 0.7,
};

// Default viewport trigger for scroll-based reveals: fire once, slightly
// before the element is fully on screen so content doesn't feel late.
export const VIEWPORT_DEFAULT = { once: true, margin: '-80px' };

export const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: DURATION.slow, ease: EASE_OUT } },
};

export const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE_OUT } },
};

export const fadeInDown = {
  hidden: { opacity: 0, y: -24 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE_OUT } },
};

export const scaleIn = {
  hidden: { opacity: 0, scale: 0.96 },
  show: { opacity: 1, scale: 1, transition: { duration: DURATION.slow, ease: EASE_OUT } },
};

export const slideInLeft = {
  hidden: { opacity: 0, x: -32 },
  show: { opacity: 1, x: 0, transition: { duration: DURATION.base, ease: EASE_OUT } },
};

export const slideInRight = {
  hidden: { opacity: 0, x: 32 },
  show: { opacity: 1, x: 0, transition: { duration: DURATION.base, ease: EASE_OUT } },
};

// Container variant that staggers its direct motion children. Pair with
// fadeInUp (or another child variant) on each child.
export const staggerContainer = (stagger = 0.08, delayChildren = 0) => ({
  hidden: {},
  show: {
    transition: { staggerChildren: stagger, delayChildren },
  },
});

// Route-level transition, used by <PageTransition> in App.js.
export const pageVariants = {
  initial: { opacity: 0, y: 12 },
  enter: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE_OUT } },
  exit: { opacity: 0, y: -8, transition: { duration: DURATION.fast, ease: EASE_IN_OUT } },
};
